// src/pages/Landing.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowUpRight,
  Phone,
  Mail,
  MapPin,
  Shield,
  Leaf,
  Flame,
  Award,
  Linkedin,
  Instagram,
  Twitter,
} from 'lucide-react';

import portalKganya from '../assets/images/teabags.jpeg';
import livestock1 from '../assets/images/slideshow/livestock-1.jpg';
import films1 from '../assets/images/slideshow/films-1.jpg';
import thandoImage from '../assets/images/team/thando-tshabalala.jpg';
import tshidisoImage from '../assets/images/team/tshidiso-swedi.jpg';
import heroGell from '../assets/images/gellcandle.jpeg';
import heroGell2 from '../assets/images/gellcandle2.jpeg';
import heroGell3 from '../assets/images/gellcandle3.jpeg';
import heroLotion from '../assets/images/lotion.jpeg';
import heroSoap1 from '../assets/images/soap1.jpeg';
import heroSoap2 from '../assets/images/soap2.jpeg';

const heroSlides = [
  {
    id: 'products',
    image: heroGell,
    title: 'Enhance Your Senses in Luxury',
    subtitle: 'Kganya Scents Flagship Collection',
    description: 'Warm, earthy fragrances inspired by botanical rituals.',
  },
  {
    id: 'gell',
    image: heroGell2,
    title: 'Light the Moments that Matter',
    subtitle: 'Hand-crafted gell candles with layered botanicals',
    description: 'Each pour unveils colour, texture, and scent in perfect harmony.',
  },
  {
    id: 'lotion',
    image: heroLotion,
    title: 'Nourish with Natural Alchemy',
    subtitle: 'Artisan lotions and oils',
    description: 'Infused with farm-grown botanicals for soft, luminous skin.',
  },
  {
    id: 'soap',
    image: heroSoap1,
    title: 'Crafted with Care',
    subtitle: 'Botanical soap bars',
    description: 'Hand-pressed bars that cleanse while indulging the senses.',
  },
  {
    id: 'ambience',
    image: heroGell3,
    title: 'Curate Ambient Rituals',
    subtitle: 'Signature jars for every room mood',
    description: 'Layer rich aromas with décor-driven vessels that glow in pure gold light.',
  },
  {
    id: 'spa',
    image: heroSoap2,
    title: 'The Spa Collection',
    subtitle: 'Scents inspired by serene sanctuaries',
    description: 'Transform daily routines into five-star moments with Kganya Scents essentials.',
  },
];

const worlds = [
  {
    name: 'Kganya Scents',
    category: 'Flagship Brand',
    description: 'Sculpted candles, botanical lotions, and luxury sensory rituals.',
    link: '/kganya-scents',
    image: portalKganya,
    gradient: 'from-brand-black/95 via-brand-black/70 to-transparent',
  },
  {
    name: 'Farming',
    category: 'Natural Ingredients',
    description: 'Ethical agriculture supplying the botanicals behind Kganya Scents.',
    link: '/farming',
      image: livestock1,
    gradient: 'from-brand-black/95 via-brand-black/70 to-transparent',
    },
    {
    name: 'Films',
    category: 'Storytelling Studio',
    description: 'Cinematic storytelling capturing the essence of every product.',
      link: '/films',
    image: films1,
    gradient: 'from-brand-black/95 via-brand-black/70 to-transparent',
  },
];

const coreValues = [
  {
    icon: Shield,
    title: 'Artisan Integrity',
    description: 'Every product is handcrafted with uncompromising standards.',
  },
  {
    icon: Leaf,
    title: 'Nature First',
    description: 'We harvest responsibly to honour the earth that inspires us.',
  },
  {
    icon: Flame,
    title: 'Sensory Innovation',
    description: 'Designing immersive experiences that ignite every sense.',
  },
  {
    icon: Award,
    title: 'Empowered Growth',
    description: 'Building businesses that uplift communities and collaborators.',
  },
];

const founders = [
  {
    id: 'thando',
    name: 'Thando Tshabalala',
    title: 'Founder & CEO',
    location: 'Soweto, Gauteng',
    image: thandoImage,
    bio: 'Thando Tshabalala is the visionary Founder and CEO of Pontso Yarona Group. She leads with purpose, blending strategy, artistry, and community upliftment.',
    quote: 'Luxury lives in the details, and so does our impact.',
    social: {
      linkedin: '#',
      instagram: '#',
      twitter: '#',
    },
  },
  {
    id: 'tshidiso',
    name: 'Tshidiso Swedi',
    title: 'Co-Founder & COO',
    location: 'Soweto, Dobsonville',
    image: tshidisoImage,
    bio: 'Tshidiso Swedi is the operational backbone of the group. His relentless drive turns bold ideas into repeatable, scalable excellence.',
    quote: 'Innovation only matters when it changes lives.',
    social: {
      linkedin: '#',
      instagram: '#',
      twitter: '#',
    },
  },
  ];

  const contactInfo = {
    phone: '+27 (0) 67 820 1034',
    email: 'info@pontsoyarona.co.za',
  address: 'Johannesburg, Gauteng',
};

/* ── Ticker items ──────────────────────────────────────── */
const tickerItems = [
  'Handcrafted in South Africa',
  'Free shipping nationwide',
  'Premium botanical ingredients',
  'Female-founded & led',
  '100% natural fragrances',
  'Artisan quality, every batch',
];

const Landing = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 1024 : false
  );
  const [currentPortalIndex, setCurrentPortalIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setCurrentSlide(p => (p + 1) % heroSlides.length), 6000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isMobile) { setCurrentPortalIndex(0); return; }
    const id = setInterval(() => setCurrentPortalIndex(p => (p + 1) % worlds.length), 7000);
    return () => clearInterval(id);
  }, [isMobile]);

  return (
    <div className="bg-brand-black text-brand-light">
      <main>

        {/* ── HERO ──────────────────────────────────────────────────── */}
        <section className="relative min-h-screen overflow-hidden flex items-center justify-center">
          {/* Slideshow background */}
          <AnimatePresence mode="wait">
            {heroSlides.map((slide, i) =>
              i === currentSlide && (
                <motion.div
                  key={slide.id}
                  className="absolute inset-0"
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.4 }}
                >
                  <img src={slide.image} alt={slide.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/55 to-brand-black/30" />
                </motion.div>
              )
            )}
          </AnimatePresence>

          {/* Hero content — centred */}
          <div className="relative z-10 text-center px-5 sm:px-8 max-w-5xl mx-auto">
            <motion.p
              key={`eyebrow-${currentSlide}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-xs font-body uppercase tracking-[0.3em] text-brand-gold/70 mb-6"
            >
              Pontso Yarona Group
            </motion.p>

            <motion.h1
              key={`h1-${currentSlide}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-[2.6rem] sm:text-6xl lg:text-[5rem] font-heading text-brand-gold leading-[1.08] tracking-tight mb-6"
            >
              {heroSlides[currentSlide].title}
            </motion.h1>

            <motion.p
              key={`sub-${currentSlide}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-base sm:text-lg text-brand-light/60 font-body max-w-xl mx-auto mb-10"
            >
              {heroSlides[currentSlide].description}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.32 }}
              className="flex flex-wrap items-center justify-center gap-4"
            >
              <Link
                to="/kganya-scents"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-brand-red text-white text-sm font-medium transition-all hover:bg-brand-red/85 hover:shadow-[0_0_40px_-8px_rgba(230,0,0,0.7)] hover:-translate-y-0.5"
              >
                Shop Kganya Scents
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#worlds"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-brand-gold/30 text-brand-gold text-sm font-medium transition-all hover:bg-brand-gold/8 hover:border-brand-gold/60"
              >
                Explore the Group
              </a>
            </motion.div>

            {/* Slide dots */}
            <div className="flex justify-center gap-2 mt-14">
              {heroSlides.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => setCurrentSlide(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={`h-[3px] rounded-full transition-all duration-500 ${
                    i === currentSlide ? 'w-10 bg-brand-red' : 'w-4 bg-white/20 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* ── TICKER ────────────────────────────────────────────────── */}
        <div className="border-y border-white/5 bg-brand-black/80 overflow-hidden py-4">
          <div className="flex gap-12 whitespace-nowrap animate-[ticker_28s_linear_infinite]">
            {[...tickerItems, ...tickerItems].map((item, i) => (
              <span key={i} className="text-xs font-body uppercase tracking-[0.25em] text-brand-light/35 flex-shrink-0">
                <span className="text-brand-gold/40 mr-6">✦</span>
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* ── WORLDS ────────────────────────────────────────────────── */}
        <section id="worlds" className="py-24 sm:py-32 px-5 sm:px-8 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="mb-16 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-brand-gold/60 mb-3">Three Pillars</p>
                <h2 className="text-4xl sm:text-5xl font-heading text-brand-light leading-tight">
                  The Pontso Yarona<br />
                  <span className="text-brand-gold">Worlds</span>
                </h2>
              </div>
              <p className="text-brand-light/50 font-body text-sm max-w-xs sm:text-right">
                One ecosystem — fragrance, farming, and film — each world amplifying the next.
              </p>
            </div>

            {/* Desktop: asymmetric grid */}
            <div className="hidden lg:grid grid-cols-3 gap-4">
              {worlds.map((world, i) => (
                <motion.div
                  key={world.name}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: i * 0.12 }}
                  viewport={{ once: true }}
                  className={`group relative overflow-hidden rounded-3xl border border-white/6 ${
                    i === 0 ? 'col-span-2 h-[580px]' : 'h-[580px]'
                  } transition-all duration-500 hover:border-brand-gold/25 hover:shadow-[0_40px_100px_-30px_rgba(255,195,0,0.2)]`}
                >
                  <Link to={world.link} className="block h-full w-full">
                    <img src={world.image} alt={world.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-black/95 via-brand-black/40 to-transparent" />
                    <div className="absolute inset-0 flex flex-col justify-end p-10">
                      <p className="text-[10px] uppercase tracking-[0.35em] text-brand-gold/70 mb-2">{world.category}</p>
                      <h3 className="text-3xl font-heading text-brand-light mb-2">{world.name}</h3>
                      <p className="text-brand-light/55 font-body text-sm max-w-sm mb-6">{world.description}</p>
                      <span className="inline-flex items-center gap-2 text-brand-gold text-sm font-medium group-hover:gap-3 transition-all">
                        Enter World <ArrowUpRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Mobile: swipeable carousel */}
            <div className="lg:hidden overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentPortalIndex * 100}%)` }}
              >
                {worlds.map((world) => (
                  <div key={world.name} className="min-w-full pr-3">
                    <Link to={world.link} className="relative block h-[420px] rounded-3xl overflow-hidden border border-white/6">
                      <img src={world.image} alt={world.name} className="h-full w-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-black/95 via-brand-black/40 to-transparent" />
                      <div className="absolute bottom-0 p-8">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-brand-gold/70 mb-2">{world.category}</p>
                        <h3 className="text-2xl font-heading text-brand-light mb-2">{world.name}</h3>
                        <p className="text-brand-light/55 text-sm mb-4">{world.description}</p>
                        <span className="inline-flex items-center gap-1 text-brand-gold text-sm font-medium">
                          Enter <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
              <div className="flex justify-center gap-2 mt-5">
                {worlds.map((_, i) => (
                  <button key={i} onClick={() => setCurrentPortalIndex(i)}
                    className={`h-[3px] rounded-full transition-all duration-300 ${i === currentPortalIndex ? 'w-8 bg-brand-red' : 'w-3 bg-white/20'}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── ABOUT + VALUES ────────────────────────────────────────── */}
        <section id="about" className="py-24 sm:py-32 px-5 sm:px-8 lg:px-12 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            {/* Headline block */}
            <div className="grid lg:grid-cols-2 gap-16 mb-20">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <p className="text-xs uppercase tracking-[0.3em] text-brand-gold/60 mb-4">About Us</p>
                <h2 className="text-4xl sm:text-5xl font-heading text-brand-light leading-tight">
                  Sensory luxury,<br />
                  <span className="text-brand-gold">rooted in purpose.</span>
                </h2>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="flex flex-col justify-center space-y-5 text-brand-light/55 font-body text-base leading-relaxed"
              >
                <p>
                  Pontso Yarona Group is a female-led collective founded in 2024. We
                  blend design, agriculture, and storytelling to create brands that feel
                  as good as they look. Kganya Scents is our flagship luxury fragrance
                  house — powered by ethical farming and cinematic narrative.
                </p>
                <a href="#contact" className="inline-flex items-center gap-2 text-brand-gold text-sm font-medium hover:gap-3 transition-all">
                  Partner with us <ArrowRight className="w-4 h-4" />
                </a>
              </motion.div>
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5 rounded-2xl overflow-hidden mb-16">
              {[
                { value: '2024', label: 'Founded' },
                { value: '3', label: 'Business verticals' },
                { value: '100%', label: 'Natural ingredients' },
                { value: 'SA', label: 'Proudly South African' },
              ].map((s) => (
                <div key={s.label} className="bg-brand-black px-6 py-8 text-center">
                  <p className="text-3xl font-heading text-brand-gold mb-1">{s.value}</p>
                  <p className="text-xs uppercase tracking-wider text-brand-light/35 font-body">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Values */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {coreValues.map((v, i) => {
                const Icon = v.icon;
                return (
                  <motion.div
                    key={v.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    viewport={{ once: true }}
                    className="group p-7 rounded-2xl border border-white/6 hover:border-brand-gold/20 transition-all duration-300 hover:bg-brand-gold/3"
                  >
                    <div className="w-10 h-10 rounded-xl bg-brand-red/10 flex items-center justify-center mb-5 group-hover:bg-brand-red/20 transition-colors">
                      <Icon className="w-5 h-5 text-brand-gold" />
                    </div>
                    <h3 className="text-base font-heading text-brand-light mb-2">{v.title}</h3>
                    <p className="text-xs text-brand-light/40 font-body leading-relaxed">{v.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── FOUNDERS ──────────────────────────────────────────────── */}
        <section id="founders" className="py-24 sm:py-32 px-5 sm:px-8 lg:px-12 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-xs uppercase tracking-[0.3em] text-brand-gold/60 mb-3">Leadership</p>
              <h2 className="text-4xl sm:text-5xl font-heading text-brand-light">
                Meet the <span className="text-brand-gold">Visionaries</span>
              </h2>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              {founders.map((f, i) => (
                <motion.div
                  key={f.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="flex gap-6 p-6 rounded-3xl border border-white/6 bg-white/2 hover:border-brand-gold/15 transition-all duration-300"
                >
                  <div className="flex-shrink-0 w-28 h-36 rounded-2xl overflow-hidden border border-white/8">
                    <img src={f.image} alt={f.name} className="h-full w-full object-cover" loading="lazy" />
                  </div>
                  <div className="flex-1 space-y-3">
                    <div>
                      <h3 className="text-xl font-heading text-brand-gold">{f.name}</h3>
                      <p className="text-xs text-brand-light/45 font-body">{f.title}</p>
                      <p className="text-xs text-brand-light/30 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />{f.location}
                      </p>
                    </div>
                    <blockquote className="text-sm italic text-brand-light/60 border-l-2 border-brand-gold/30 pl-3">
                      "{f.quote}"
                    </blockquote>
                    <p className="text-xs text-brand-light/40 font-body leading-relaxed line-clamp-3">{f.bio}</p>
                    <div className="flex gap-2 pt-1">
                      {Object.entries(f.social).map(([platform, url]) => (
                        <a key={platform} href={url}
                          className="w-8 h-8 flex items-center justify-center rounded-lg border border-white/8 text-brand-light/30 hover:text-brand-gold hover:border-brand-gold/30 transition-all"
                        >
                          {platform === 'linkedin' && <Linkedin className="w-3.5 h-3.5" />}
                          {platform === 'instagram' && <Instagram className="w-3.5 h-3.5" />}
                          {platform === 'twitter' && <Twitter className="w-3.5 h-3.5" />}
                        </a>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CONTACT ───────────────────────────────────────────────── */}
        <section id="contact" className="py-24 sm:py-32 px-5 sm:px-8 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-14"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-brand-gold/60 mb-4">Get in Touch</p>
              <h2 className="text-4xl sm:text-5xl font-heading text-brand-light mb-4">
                Let's create together
              </h2>
              <p className="text-brand-light/45 font-body text-base max-w-lg mx-auto">
                Wholesale enquiries, collaborations, or just to say hello — we'd love to hear from you.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="grid sm:grid-cols-3 gap-4 mb-12"
            >
              {[
                { Icon: Phone, label: 'Call us', value: contactInfo.phone, href: `tel:${contactInfo.phone}` },
                { Icon: Mail,  label: 'Email',   value: contactInfo.email,  href: `mailto:${contactInfo.email}` },
                { Icon: MapPin,label: 'Location', value: contactInfo.address,href: '#' },
              ].map(({ Icon, label, value, href }) => (
                <a key={label} href={href}
                  className="group flex flex-col items-center gap-3 p-7 rounded-2xl border border-white/6 hover:border-brand-gold/20 hover:bg-brand-gold/3 transition-all duration-300 text-center"
                >
                  <div className="w-10 h-10 rounded-xl bg-brand-red/10 flex items-center justify-center group-hover:bg-brand-red/20 transition-colors">
                    <Icon className="w-5 h-5 text-brand-gold" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-brand-light/30 mb-1">{label}</p>
                    <p className="text-sm text-brand-light/70 group-hover:text-brand-light transition-colors">{value}</p>
                  </div>
                </a>
              ))}
            </motion.div>

            <div className="text-center">
              <a
                href="mailto:info@pontsoyarona.co.za"
                className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-brand-red text-white text-sm font-medium transition-all hover:bg-brand-red/85 hover:shadow-[0_0_40px_-8px_rgba(230,0,0,0.7)]"
              >
                <Mail className="w-4 h-4" />
                Send us a message
              </a>
            </div>
          </div>
        </section>

      </main>
    </div>
  );
};

export default Landing;


